import React, { useState, useEffect } from 'react'
import { Modal, Button, Table, Badge, Spinner, Form } from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { SYNC_MEMBER_TO_HEALTHSHERPA, SEARCH_HEALTHSHERPA_CONTACT } from '../../../gql/queries/policiesApiQueries'
import { GET_ALL_USERS_FILTER } from '../../../gql/queries/usersQuery'
import { IHealthSherpaContact } from '../../../types/policiesApi'
import { useGetUser } from '../../../store/userStore'
import { toast } from 'react-toastify'

type SearchField = 'birth_date' | 'phone' | 'email'

const M = defineMessages({
  titleResync: {id: 'policies.list.hsSearch.titleResync', defaultMessage: 'HealthSherpa: Re-Sync'},
  titleMatches: {id: 'policies.list.hsSearch.titleMatches', defaultMessage: 'HealthSherpa: Matches found'},
  localMember: {id: 'policies.list.hsSearch.localMember', defaultMessage: 'Local Member:'},
  radioDob: {id: 'policies.list.hsSearch.radio.dob', defaultMessage: 'DOB: {value}'},
  radioPhone: {id: 'policies.list.hsSearch.radio.phone', defaultMessage: 'Phone: {value}'},
  radioEmail: {id: 'policies.list.hsSearch.radio.email', defaultMessage: 'Email: {value}'},
  agentForQuery: {id: 'policies.list.hsSearch.agentForQuery', defaultMessage: 'Agent for this query:'},
  agentOption: {id: 'policies.list.hsSearch.agentOption', defaultMessage: '{name} — {email} (NPN: {npn})'},
  searching: {id: 'policies.list.hsSearch.searching', defaultMessage: 'Searching...'},
  search: {id: 'policies.list.hsSearch.search', defaultMessage: 'Search'},
  introResync: {id: 'policies.list.hsSearch.introResync', defaultMessage: 'Review the HealthSherpa matches, compare them against the local record, and choose what you want to re-sync.'},
  introMatches: {id: 'policies.list.hsSearch.introMatches', defaultMessage: "The following HealthSherpa contacts match this member's information. Select one to link it, or create a new contact if none match."},
  thNameHs: {id: 'policies.list.hsSearch.th.nameHs', defaultMessage: 'Name in HealthSherpa'},
  thDob: {id: 'policies.list.hsSearch.th.dob', defaultMessage: 'DOB'},
  thPhone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  thEmail: {id: 'common.field.email', defaultMessage: 'Email'},
  thStatus: {id: 'common.field.status', defaultMessage: 'Status'},
  thAction: {id: 'policies.list.hsSearch.th.action', defaultMessage: 'Action'},
  badgeSelected: {id: 'policies.list.hsSearch.badgeSelected', defaultMessage: 'Selected'},
  currentlyLinked: {id: 'policies.list.hsSearch.currentlyLinked', defaultMessage: 'Currently linked in HS'},
  statusActive: {id: 'policies.list.hsSearch.statusActive', defaultMessage: 'Active'},
  reSync: {id: 'policies.list.hsSearch.reSync', defaultMessage: 'Re-Sync'},
  link: {id: 'policies.list.hsSearch.link', defaultMessage: 'Link'},
  noMatches: {id: 'policies.list.hsSearch.noMatches', defaultMessage: 'No exact matches found.'},
  localVsHs: {id: 'policies.list.hsSearch.localVsHs', defaultMessage: 'Local vs HealthSherpa'},
  diffCount: {id: 'policies.list.hsSearch.diffCount', defaultMessage: '{count, plural, one {# difference} other {# differences}}'},
  noDifferences: {id: 'policies.list.hsSearch.noDifferences', defaultMessage: 'No differences'},
  thField: {id: 'policies.list.hsSearch.th.field', defaultMessage: 'Field'},
  thLocal: {id: 'policies.list.hsSearch.th.local', defaultMessage: 'Local'},
  thHealthSherpa: {id: 'policies.list.hsSearch.th.healthSherpa', defaultMessage: 'HealthSherpa'},
  cmpFirstName: {id: 'common.field.firstName', defaultMessage: 'First Name'},
  cmpLastName: {id: 'common.field.lastName', defaultMessage: 'Last Name'},
  cmpDob: {id: 'common.field.dateOfBirth', defaultMessage: 'Date of Birth'},
  cmpPhone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  cmpEmail: {id: 'common.field.email', defaultMessage: 'Email'},
  cmpAddress1: {id: 'policies.list.hsSearch.cmp.address1', defaultMessage: 'Address 1'},
  cmpAddress2: {id: 'policies.list.hsSearch.cmp.address2', defaultMessage: 'Address 2'},
  cmpCity: {id: 'policies.list.hsSearch.cmp.city', defaultMessage: 'City'},
  cmpState: {id: 'policies.list.hsSearch.cmp.state', defaultMessage: 'State'},
  cmpZip: {id: 'policies.list.hsSearch.cmp.zip', defaultMessage: 'ZIP Code'},
  cmpSex: {id: 'policies.list.hsSearch.cmp.sex', defaultMessage: 'Sex'},
  notesTitle: {id: 'policies.list.hsSearch.notesTitle', defaultMessage: 'Notes for HealthSherpa'},
  notesPlaceholder: {id: 'policies.list.hsSearch.notesPlaceholder', defaultMessage: 'Add notes to send when creating a new HealthSherpa contact.'},
  reSyncSelected: {id: 'policies.list.hsSearch.reSyncSelected', defaultMessage: 'Re-Sync Selected Match'},
  createNew: {id: 'policies.list.hsSearch.createNew', defaultMessage: 'Create New Contact'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  toastSelectAgent: {id: 'policies.list.hsSearch.toast.selectAgent', defaultMessage: 'Select an agent before searching.'},
  toastNoField: {id: 'policies.list.hsSearch.toast.noField', defaultMessage: 'This member does not have {field} available.'},
  toastSearchError: {id: 'policies.list.hsSearch.toast.searchError', defaultMessage: 'Search error: {message}'},
  toastSyncSuccess: {id: 'policies.list.hsSearch.toast.syncSuccess', defaultMessage: 'Sync successful'},
  toastSyncFailed: {id: 'policies.list.hsSearch.toast.syncFailed', defaultMessage: 'Sync failed'},
  fieldDob: {id: 'policies.list.hsSearch.field.dob', defaultMessage: 'date of birth'},
  fieldPhone: {id: 'policies.list.hsSearch.field.phone', defaultMessage: 'phone'},
  fieldEmail: {id: 'policies.list.hsSearch.field.email', defaultMessage: 'email'},
})

interface ModalHealthSherpaSearchProps {
  isOpen: boolean
  onClose: () => void
  member: {
    id: string
    firstName: string
    lastName: string
    dateBirth: string
    phone?: string | null
    email?: string | null
    healthsherpaContactId?: string | null
    address1?: string | null
    address2?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    gender?: string | null
  }
  searchResults: IHealthSherpaContact[]
  onSuccess: (contactId: string) => void
  agentEmail: string
}

export const ModalHealthSherpaSearch: React.FC<ModalHealthSherpaSearchProps> = ({
  isOpen,
  onClose,
  member,
  searchResults,
  onSuccess,
  agentEmail,
}) => {
  const intl = useIntl()
  const emDash = '—' // non-translatable empty-value placeholder (hoisted)
  const [syncMember, { loading }] = useMutation(SYNC_MEMBER_TO_HEALTHSHERPA)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [currentAgentEmail, setCurrentAgentEmail] = useState(agentEmail)
  const [currentResults, setCurrentResults] = useState<IHealthSherpaContact[]>(searchResults)
  const [isSearching, setIsSearching] = useState(false)
  const [searchField, setSearchField] = useState<SearchField>('birth_date')
  const [selectedHsContactId, setSelectedHsContactId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)

  const currentUser = useGetUser((s) => s.user?.user)
  const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
  const isAdmin = userGroups.includes('Administrador') || userGroups.includes('SuperAdministrador')
  const isAgent = userGroups.includes('Agente')
  const sessionAgentEmail = currentUser?.email || ''
  const isResyncMode = Boolean(member.healthsherpaContactId)

  const { data: usersData } = useQuery(GET_ALL_USERS_FILTER, {
    variables: { requireNpn: true },
    skip: !isOpen || !isAdmin,
  })
  const agentUsers: Array<{ id: string; firstName: string; lastName: string; email: string; npn: string }> =
    usersData?.allUsers?.edges?.map((e: any) => e.node) || []

  const [reSearch] = useLazyQuery(SEARCH_HEALTHSHERPA_CONTACT, {
    onCompleted: (data) => {
      setCurrentResults(data?.searchHealthsherpaContact?.contacts || [])
      setIsSearching(false)
    },
    onError: (err) => {
      toast.error(intl.formatMessage(M.toastSearchError, {message: err.message}))
      setCurrentResults([])
      setIsSearching(false)
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    setCurrentAgentEmail(agentEmail)
  }, [agentEmail])

  useEffect(() => {
    if (!isOpen || !isAgent || !sessionAgentEmail) return

    setCurrentAgentEmail(sessionAgentEmail)
  }, [isOpen, isAgent, sessionAgentEmail])

  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false)
      return
    }

    if (!isInitialized && isAdmin && sessionAgentEmail && agentUsers.length > 0) {
      const isInList = agentUsers.some((u) => u.email === sessionAgentEmail)
      if (isInList && !currentAgentEmail) {
        setCurrentAgentEmail(sessionAgentEmail)
      }
      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitialized])

  useEffect(() => {
    if (!isOpen) return
    setCurrentResults(searchResults)
    setSearchField('birth_date')
    setIsSearching(false)
    setSelectedHsContactId(null)
    setNotes('')
  }, [isOpen, searchResults, member.id])

  useEffect(() => {
    if (currentResults.length === 0) {
      setSelectedHsContactId(null)
      return
    }

    const existingMatch = member.healthsherpaContactId
      ? currentResults.find((contact) => contact.id === member.healthsherpaContactId)
      : null

    setSelectedHsContactId(existingMatch?.id || currentResults[0]?.id || null)
  }, [currentResults, member.healthsherpaContactId])

  const getSearchValue = () => {
    if (searchField === 'birth_date') return member.dateBirth
    if (searchField === 'phone') return member.phone
    return member.email
  }

  const handleSearch = () => {
    const selectedValue = getSearchValue()

    if (!currentAgentEmail) {
      toast.error(intl.formatMessage(M.toastSelectAgent))
      return
    }

    if (!selectedValue) {
      const fieldMsg =
        searchField === 'birth_date' ? M.fieldDob : searchField === 'phone' ? M.fieldPhone : M.fieldEmail
      toast.error(intl.formatMessage(M.toastNoField, {field: intl.formatMessage(fieldMsg)}))
      return
    }

    setIsSearching(true)
    setCurrentResults([])
    reSearch({
      variables: {
        memberId: parseInt(member.id),
        agentEmail: currentAgentEmail,
        searchField,
      },
    })
  }

  const selectedHsContact = currentResults.find((contact) => contact.id === selectedHsContactId) || null

  const normalizePhoneForComparison = (value?: string | null) => {
    const digits = (value || '').replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1)
    return digits
  }

  const normalizeValue = (value?: string | null) => (value || '').trim()

  const comparisonRows = selectedHsContact
    ? [
      {field: 'firstName', label: intl.formatMessage(M.cmpFirstName), local: member.firstName || emDash, hs: selectedHsContact.firstName || emDash},
      {field: 'lastName', label: intl.formatMessage(M.cmpLastName), local: member.lastName || emDash, hs: selectedHsContact.lastName || emDash},
      {field: 'dateOfBirth', label: intl.formatMessage(M.cmpDob), local: member.dateBirth || emDash, hs: selectedHsContact.dateOfBirth || emDash},
      {field: 'phone', label: intl.formatMessage(M.cmpPhone), local: member.phone || emDash, hs: selectedHsContact.phone || emDash},
      {field: 'email', label: intl.formatMessage(M.cmpEmail), local: member.email || emDash, hs: selectedHsContact.email || emDash},
      {field: 'address1', label: intl.formatMessage(M.cmpAddress1), local: member.address1 || emDash, hs: selectedHsContact.address1 || emDash},
      {field: 'address2', label: intl.formatMessage(M.cmpAddress2), local: member.address2 || emDash, hs: selectedHsContact.address2 || emDash},
      {field: 'city', label: intl.formatMessage(M.cmpCity), local: member.city || emDash, hs: selectedHsContact.city || emDash},
      {field: 'state', label: intl.formatMessage(M.cmpState), local: member.state || emDash, hs: selectedHsContact.state || emDash},
      {field: 'zip', label: intl.formatMessage(M.cmpZip), local: member.zip || emDash, hs: selectedHsContact.zip || emDash},
      {field: 'sex', label: intl.formatMessage(M.cmpSex), local: member.gender || emDash, hs: selectedHsContact.gender || emDash},
    ].map((row) => ({
      ...row,
      isDifferent:
        row.field === 'phone'
          ? normalizePhoneForComparison(row.local) !== normalizePhoneForComparison(row.hs)
          : normalizeValue(row.local) !== normalizeValue(row.hs),
    }))
    : []

  const differencesCount = comparisonRows.filter((row) => row.isDifferent).length

  const handleSync = async (contactId: string | null) => {
    setSyncingId(contactId || 'new')
    try {
      const { data } = await syncMember({
        variables: {
          memberId: parseInt(member.id),
          healthsherpaContactId: contactId,
          agentEmail: currentAgentEmail,
          notes: notes.trim() || null,
          resync: Boolean(contactId && isResyncMode),
        },
      })

      if (data?.syncMemberToMedicareHealthsherpa?.success) {
        toast.success(data.syncMemberToMedicareHealthsherpa.message || intl.formatMessage(M.toastSyncSuccess))
        onSuccess(data.syncMemberToMedicareHealthsherpa.healthsherpaContactId)
        onClose()
      } else {
        toast.error(data?.syncMemberToMedicareHealthsherpa?.message || intl.formatMessage(M.toastSyncFailed))
      }
    } catch (error: any) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    } finally {
      setSyncingId(null)
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="text-primary font-weight-bold">
          <i className="flaticon-search-1 mr-2"></i>
          {isResyncMode ? intl.formatMessage(M.titleResync) : intl.formatMessage(M.titleMatches)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {/* Local member */}
        <div className="mb-3 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">{intl.formatMessage(M.localMember)}</h6>
          <div className="d-flex align-items-start">
            <div className="symbol symbol-40 symbol-light-primary mr-3 font-weight-bold">
              <span className="symbol-label font-size-h5">
                {member.firstName[0]}
                {member.lastName[0]}
              </span>
            </div>
            <div className="flex-grow-1">
              <div className="text-dark-75 font-weight-bolder font-size-lg">
                {member.firstName} {member.lastName}
              </div>
              <div className="mt-3 d-flex flex-wrap gap-3">
                <Form.Check
                  type="radio"
                  id={`hs-search-birth-date-${member.id}`}
                  name="hs-search-field"
                  label={intl.formatMessage(M.radioDob, {value: member.dateBirth || emDash})}
                  inline
                  checked={searchField === 'birth_date'}
                  onChange={() => setSearchField('birth_date')}
                />
                <Form.Check
                  type="radio"
                  id={`hs-search-phone-${member.id}`}
                  name="hs-search-field"
                  label={intl.formatMessage(M.radioPhone, {value: member.phone || emDash})}
                  inline
                  checked={searchField === 'phone'}
                  onChange={() => setSearchField('phone')}
                />
                <Form.Check
                  type="radio"
                  id={`hs-search-email-${member.id}`}
                  name="hs-search-field"
                  label={intl.formatMessage(M.radioEmail, {value: member.email || emDash})}
                  inline
                  checked={searchField === 'email'}
                  onChange={() => setSearchField('email')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Agent section */}
        <div className="mb-4 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">{intl.formatMessage(M.agentForQuery)}</h6>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="flex-grow-1">
              {isAdmin && agentUsers.length > 0 ? (
                <Form.Select
                  size="sm"
                  value={currentAgentEmail}
                  onChange={(e) => setCurrentAgentEmail(e.target.value)}
                  disabled={isSearching}
                >
                  {agentUsers.map((u) => (
                    <option key={u.id} value={u.email}>
                      {intl.formatMessage(M.agentOption, {name: `${u.firstName} ${u.lastName}`, email: u.email, npn: u.npn})}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <div className="text-dark-75 font-weight-bold font-size-sm border rounded px-3 py-2 bg-white">
                  <i className="bi bi-person-badge me-1" />
                  {currentAgentEmail || emDash}
                </div>
              )}
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleSearch}
              disabled={isSearching || loading}
            >
              {isSearching ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {intl.formatMessage(M.searching)}
                </>
              ) : (
                <>
                  <i className="flaticon-search-1 mr-1"></i>
                  {intl.formatMessage(M.search)}
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-dark-50 mb-4">
          {isResyncMode ? intl.formatMessage(M.introResync) : intl.formatMessage(M.introMatches)}
        </p>

        <div className="table-responsive">
          <Table hover className="border">
            <thead className="thead-light font-weight-bold text-muted text-uppercase font-size-sm">
              <tr>
                <th>{intl.formatMessage(M.thNameHs)}</th>
                <th>{intl.formatMessage(M.thDob)}</th>
                <th>{intl.formatMessage(M.thPhone)}</th>
                <th>{intl.formatMessage(M.thEmail)}</th>
                <th className="text-center">{intl.formatMessage(M.thStatus)}</th>
                <th className="text-right">{intl.formatMessage(M.thAction)}</th>
              </tr>
            </thead>
            <tbody>
              {isSearching ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : currentResults.length > 0 ? (
                currentResults.map((contact) => (
                  <tr
                    key={contact.id}
                    className="align-middle"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedHsContactId(contact.id)}
                  >
                    <td>
                      <div
                        className="d-flex flex-column"
                        style={{
                          padding: selectedHsContactId === contact.id ? '0.4rem 0.5rem' : undefined,
                          borderRadius: selectedHsContactId === contact.id ? '0.5rem' : undefined,
                          backgroundColor: selectedHsContactId === contact.id ? '#eef6ff' : 'transparent',
                          border: selectedHsContactId === contact.id ? '1px solid #b8d8ff' : '1px solid transparent',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="font-weight-bolder text-dark-75">{contact.firstName} {contact.lastName}</div>
                          {selectedHsContactId === contact.id && (
                            <Badge bg="primary">{intl.formatMessage(M.badgeSelected)}</Badge>
                          )}
                        </div>
                        {member.healthsherpaContactId === contact.id && (
                          <div className="text-success font-size-xs mt-1 font-weight-bold">{intl.formatMessage(M.currentlyLinked)}</div>
                        )}
                      </div>
                    </td>
                    <td>{contact.dateOfBirth}</td>
                    <td>{contact.phone || emDash}</td>
                    <td>{contact.email || emDash}</td>
                    <td className="text-center">
                      <Badge bg="info">
                        {contact.status || intl.formatMessage(M.statusActive)}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="font-weight-bolder px-4"
                        disabled={loading}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleSync(contact.id)
                        }}
                      >
                        {syncingId === contact.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          isResyncMode ? intl.formatMessage(M.reSync) : intl.formatMessage(M.link)
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted italic">
                    {intl.formatMessage(M.noMatches)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {selectedHsContact && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-muted mb-0 font-size-sm text-uppercase letter-spacing-1">
                {intl.formatMessage(M.localVsHs)}
              </h6>
              <Badge bg={differencesCount > 0 ? 'warning' : 'success'} text={differencesCount > 0 ? 'dark' : undefined}>
                {differencesCount > 0 ? intl.formatMessage(M.diffCount, {count: differencesCount}) : intl.formatMessage(M.noDifferences)}
              </Badge>
            </div>
            <Table bordered responsive size="sm" className="mb-0 bg-white">
              <thead>
                <tr>
                  <th>{intl.formatMessage(M.thField)}</th>
                  <th>{intl.formatMessage(M.thLocal)}</th>
                  <th>{intl.formatMessage(M.thHealthSherpa)}</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.field} className={row.isDifferent ? 'table-warning' : ''}>
                    <td className="fw-bold">{row.label}</td>
                    <td>{row.local}</td>
                    <td>{row.hs}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-4 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2 font-size-sm text-uppercase letter-spacing-1">{intl.formatMessage(M.notesTitle)}</h6>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={intl.formatMessage(M.notesPlaceholder)}
            disabled={loading || isSearching}
          />

        </div>
      </Modal.Body>
      <Modal.Footer className="bg-light d-flex justify-content-between">
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button
          variant="primary"
          className="font-weight-bolder px-5"
          disabled={loading || isSearching || (isResyncMode && !selectedHsContactId)}
          onClick={() => handleSync(isResyncMode ? selectedHsContactId : null)}
        >
          {syncingId === 'new' ? (
            <Spinner animation="border" size="sm" className="mr-2" />
          ) : (
            <i className={isResyncMode ? "bi bi-arrow-repeat mr-1" : "flaticon2-plus mr-1"}></i>
          )}
          {isResyncMode ? intl.formatMessage(M.reSyncSelected) : intl.formatMessage(M.createNew)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
